import { toast } from "@/components/pop-ui/pop-toast"

// 错误码到前端错误类型的映射
const ERROR_CODE_TO_FRONTEND_TYPE = new Map<string, string>([
	// 账户相关错误
	["ACCOUNT_PROVIDER_ALREADY_LINKED", "account_provider_already_linked"],
	["ACCOUNT_ALREADY_EXISTS_IN_AREA", "account_already_exists_in_area"],
	[
		"ACCOUNT_PROVIDER_ALREADY_LINKED_FOR_USER",
		"account_provider_already_linked_for_user",
	],

	// 用户相关错误
	["USER_BLACKLISTED", "user_blacklisted"],
	["EMAIL_BLACKLISTED", "email_blacklisted"],
	["PHONE_BLACKLISTED", "phone_blacklisted"],
	["BANNED_USER", "banned"],

	// 验证相关错误
	["TONGDUN_SIGN_VERIFICATION_FAILED", "tongdun_verification_failed"],
	["MISSING_TONGDUN_HEADERS", "missing_tongdun_headers"],
	["CAPTCHA_VERIFY_FAILED", "captcha_verification_failed"],

	// OAuth 相关错误
	["OAUTH_TOKEN_EXCHANGE_FAILED", "oauth_token_exchange_failed"],
	["OAUTH_USERINFO_FAILED", "oauth_userinfo_failed"],
	["OAUTH_PROFILE_INVALID", "oauth_profile_invalid"],
	["OAUTH_PROFILE_MAPPING_FAILED", "oauth_profile_mapping_failed"],

	// 注册/登录相关错误
	["USER_NOT_FOUND", "user_not_found"],
	["SIGN_UP_DISABLED", "sign_up_disabled"],
	["PASSWORD_NOT_SET", "password_not_set"],
	["INVALID_PASSWORD", "invalid_password"],
	["OTP_INVALID", "otp_invalid"],
	["EMAIL_OTP_RATE_LIMITED", "email_otp_rate_limited"],

	// 输入验证错误
	["INVALID_EMAIL_FORMAT", "invalid_email_format"],
	["INVALID_PHONE_FORMAT", "invalid_phone_format"],
	["EMPTY_FIELDS", "empty_fields"],
	["EMPTY_EMAIL", "empty_email"],
	["EMPTY_AREA", "empty_area"],

	// 区域相关错误
	["MISSING_AREA_HEADERS", "missing_area_headers"],
	["MISSING_LANG_HEADERS", "missing_lang_headers"],
	["MISSING_OAUTH_PARAMS", "missing_oauth_params"],
	["INVALID_PROVIDER", "invalid_provider"],
	["UNSUPPORTED_PROVIDER", "unsupported_provider"],

	// 链接模式错误
	["MISSING_LINK_USER_ID", "missing_link_user_id"],

	// 通用错误
	["UNAUTHORIZED", "unauthorized"],
	["FORBIDDEN", "forbidden"],
	["BAD_REQUEST", "bad_request"],
	["INTERNAL_SERVER_ERROR", "internal_server_error"],
])

/**
 * 根据后端错误码获取前端错误类型
 * @param errorCode 后端错误码
 * @returns 前端错误类型，如果没有映射则返回 undefined
 */
export function getFrontendErrorType(
	errorCode: string | number,
): string | undefined {
	return ERROR_CODE_TO_FRONTEND_TYPE.get(String(errorCode))
}

// 标准错误数据格式
export interface StandardErrorData {
	code: string | number
	message: string
	_isBusinessErrorToasted?: boolean
	frontendType?: string
}

// 可能的错误响应格式
export interface ErrorResponse {
	json?: StandardErrorData
	code?: string | number
	message?: string
	name?: string
}

// 错误处理选项
export interface ErrorHandlerOptions {
	showToast?: boolean
	defaultMessage?: string
	networkErrorMessage?: string
	ignoreUnauthorized?: boolean
	ignoreCodes?: (string | number)[]
}

// 懒加载auth方法，避免循环引用
let getAuthMethodsLazy: (() => any) | null = null

/**
 * 设置auth方法的懒加载函数
 */
export function setAuthMethodsLoader(loader: () => any) {
	getAuthMethodsLazy = loader
}

/**
 * 从错误中提取标准错误数据
 */
function extractStandardErrorData(error: any): StandardErrorData | null {
	if (!error) return null

	// 格式1: ErrorResponse {json: {code: xxx, message: 'yyy'}}
	if (error.json && typeof error.json === "object") {
		const { code, message } = error.json
		if (code !== undefined && message) {
			const frontendType = getFrontendErrorType(code)
			return { code, message, frontendType }
		}
	}

	// 格式2: StandardErrorData {code: xxx, message: 'yyy'}
	if (error.code !== undefined && error.message) {
		const frontendType = getFrontendErrorType(error.code)
		return {
			code: error.code,
			message: error.message,
			frontendType,
		}
	}

	return null
}

/**
 * 检查是否为未授权错误（内部使用）
 */
function isUnauthorizedErrorInternal(errorData: StandardErrorData): boolean {
	const message = errorData.message?.toLowerCase()
	const code = String(errorData.code).toLowerCase()

	return (
		errorData.code === 401 ||
		errorData.code === "401" ||
		message === "unauthorized" ||
		code === "unauthorized" ||
		message?.includes("unauthorized")
	)
}

/**
 * 检查是否为用户不存在错误
 */
function isUserNotFoundError(errorData: StandardErrorData): boolean {
	const code = String(errorData.code).toLowerCase()
	const frontendType = errorData.frontendType
	return code === "user:not_found" || frontendType === "user_not_found"
}

/**
 * 检查是否为用户被封禁错误
 */
function isBannedUserError(errorData: StandardErrorData): boolean {
	const frontendType = errorData.frontendType
	return frontendType === "banned" || frontendType === "user_blacklisted"
}

/**
 * 处理用户不存在错误 - 执行退出登录
 */
function handleUserNotFoundError(_errorData: StandardErrorData) {
	try {
		if (getAuthMethodsLazy) {
			const authMethods = getAuthMethodsLazy()
			authMethods?.logout?.({
				redirectUrl: "/user/login",
				clearQueryCache: true,
				showToast: false,
			})
		}
	} catch (error) {
		console.error("处理用户不存在错误时发生异常:", error)
	}
}

/**
 * 检查是否为请求取消错误
 */
function isAbortError(error: any): boolean {
	return (
		error &&
		typeof error === "object" &&
		"name" in error &&
		error.name === "AbortError"
	)
}

/**
 * 检查是否为网络错误（包括浏览器阻止、网络连接失败等）
 */
export function isNetworkError(error: any): boolean {
	if (!error || typeof error !== "object") return false

	const message = error.message?.toLowerCase() || ""
	const name = error.name?.toLowerCase() || ""

	return (
		// Failed to fetch - 常见的网络错误
		message.includes("failed to fetch") ||
		// Network error - 网络错误
		message.includes("network error") ||
		// TypeError: Network request failed - React Native 等环境
		message.includes("network request failed") ||
		// 连接被拒绝
		message.includes("connection refused") ||
		// 连接超时
		message.includes("timeout") ||
		// DNS 解析失败
		message.includes("dns") ||
		// 浏览器阻止请求
		message.includes("blocked") ||
		// CORS 错误
		message.includes("cors") ||
		// TypeError 且与网络相关
		(name === "typeerror" &&
			(message.includes("fetch") ||
				message.includes("network") ||
				message.includes("xhr")))
	)
}

/**
 * 检查是否为维护模式错误
 * @param data 服务器返回的数据
 * @returns 是否为维护模式错误
 */
export function isMaintenanceError(data: {
	code?: string
	json?: { code?: string }
}): boolean {
	// 检查直接的 code 字段
	if (data?.code === "SERVICE_MAINTENANCE_MODE") {
		return true
	}
	// 检查嵌套在 json 字段中的 code
	if (data?.json && data.json.code === "SERVICE_MAINTENANCE_MODE") {
		return true
	}
	return false
}

/**
 * 检查是否为未授权错误的辅助函数
 * @param data 服务器返回的数据
 * @param status HTTP 状态码
 * @returns 是否为未授权错误
 */
export function isUnauthorizedError(
	data: {
		code?: string
		json?: { code?: string }
	},
	status?: number,
): boolean {
	// 检查 HTTP 状态码是否为 401
	if (status === 401) {
		return true
	}
	// 检查直接的 code 字段
	if (data?.code === "Unauthorized") {
		return true
	}
	// 检查嵌套在 json 字段中的 code
	if (data?.json && data.json.code === "Unauthorized") {
		return true
	}
	return false
}

/**
 * 重定向到维护页面的函数
 */
export function redirectToMaintenancePage() {
	if (typeof window !== "undefined") {
		// 检查当前是否已经在停服页面，避免重复重定向
		if (window.location.pathname.includes("/shutdown")) {
			return
		}

		const pathParts = window.location.pathname.split("/")
		const locale = pathParts[1] || "hk" // 默认为香港地区
		const shutdownPath = `/${locale}/shutdown`

		window.location.href = shutdownPath
	}
}

/**
 * 重定向到登录页面的函数
 */
export function redirectToLoginPage() {
	if (typeof window !== "undefined") {
		// 检查当前是否已经在登录页面，避免重复重定向
		if (window.location.pathname.includes("/user/login")) {
			return
		}

		const pathParts = window.location.pathname.split("/")
		const locale = pathParts[1] || "hk" // 默认为香港地区
		const loginPath = `/${locale}/user/login`

		window.location.href = loginPath
	}
}

/**
 * 统一的错误处理函数
 * 用于处理业务错误格式，会toast消息并重新抛出错误
 *
 * @param error 错误对象
 * @param options 处理选项
 * @throws 如果是业务错误格式，处理后重新抛出
 */
export function handleBusinessError(
	error: any,
	options: ErrorHandlerOptions = {},
): any {
	const {
		showToast = true,
		ignoreUnauthorized = true,
		ignoreCodes = [],
	} = options

	// 处理 AbortError (请求取消) - 直接重新抛出
	if (isAbortError(error)) {
		throw error
	}

	// 检查是否忽略未授权错误
	if (
		ignoreUnauthorized &&
		(error.status === 401 || error.json?.status === 401)
	) {
		throw error
	}

	// 提取标准错误数据
	const errorData = extractStandardErrorData(error)
	if (!errorData) {
		if (error instanceof Error) {
			// 无法提取，直接重新抛出
			throw error
		}
		// 正常的业务数据
		return error
	}

	// 检查是否为用户不存在错误
	if (isUserNotFoundError(errorData)) {
		handleUserNotFoundError(errorData)
	}

	// 检查是否为用户被封禁错误
	if (isBannedUserError(errorData)) {
		// 可以在这里添加特殊的封禁用户处理逻辑
	}

	// 检查是否需要忽略特定错误码
	if (ignoreCodes.includes(errorData.code)) {
		throw errorData
	}

	// 检查是否忽略未授权错误
	if (ignoreUnauthorized && isUnauthorizedErrorInternal(errorData)) {
		throw errorData
	}

	// 显示错误提示
	if (showToast && errorData.message) {
		if (!errorData._isBusinessErrorToasted) {
			toast.error(errorData.message)
			errorData._isBusinessErrorToasted = true
		}

		// 重新抛出标准化的错误数据
		throw errorData
	}
}
